'use client';

/**
 * First-run setup (design §04 "first run").
 *
 * Shown at / to signed-in users with no workspace yet: a compact card
 * (workspace name prefilled, Terms checkbox, Turnstile) that provisions the
 * personal workspace in the background, then opens straight into chat with
 * the 500 welcome credits already there.
 *
 * A 409-with-result (workspace already exists, e.g. response lost on a prior
 * attempt) is treated as success: activate the existing workspace and move on.
 */

import { useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { suggestedWorkspaceName } from '@/lib/format';
import { newIdempotencyKey } from '@/lib/uuid';
import type { ProvisionResult } from '../api';
import { usePersonalWorkspace } from '../hooks/use-personal-workspace';
import { FRESH_WORKSPACE_KEY } from './fresh-workspace-banner';
import { TurnstileWidget, type TurnstileHandle } from './turnstile-widget';

export function FirstRunSetup() {
  const router = useRouter();
  const { user, isLoaded: userLoaded } = useUser();
  const { provisionWorkspace, activateWorkspace } = usePersonalWorkspace();

  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const fullName = user?.fullName ?? '';

  // Stable per mount: retries reuse the key (idempotent); a remount mints a
  // new one, and the backend's 409-with-result converges to the same outcome.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const [workspaceName, setWorkspaceName] = useState(() =>
    suggestedWorkspaceName({ fullName, email })
  );
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'provisioning' | 'activating'>('form');
  const turnstileRef = useRef<TurnstileHandle>(null);

  const enterApp = async (result: ProvisionResult) => {
    setPhase('activating');
    const opened = await activateWorkspace(result.clerk_organization_id);
    if (!opened) {
      setPhase('form');
      setError('Your workspace is ready, but we could not open it. Please refresh and try again.');
      return;
    }
    try {
      window.sessionStorage.setItem(FRESH_WORKSPACE_KEY, result.workspace_name);
    } catch {
      // ignore: banner is a nicety
    }
    router.replace('/chat');
  };

  const handleSubmit = async () => {
    const name = workspaceName.trim();
    if (!name) {
      setError('Give your workspace a name.');
      return;
    }
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service to continue.');
      return;
    }
    const token = turnstileRef.current?.getToken() ?? '';
    if (!token) {
      setError('Bot verification is still loading. Please wait a moment and try again.');
      return;
    }
    setPhase('provisioning');
    setError(null);
    const outcome = await provisionWorkspace({
      workspace_name: name,
      idempotency_key: idempotencyKey,
      turnstile_token: token,
      full_name: fullName,
    });
    turnstileRef.current?.reset();
    if (!outcome.ok) {
      setPhase('form');
      setError(outcome.message);
      return;
    }
    await enterApp(outcome.result);
  };

  const busy = phase !== 'form';

  if (!userLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--band)] p-6">
        <p className="text-sm text-[var(--body)]">Loading your account…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--band)] p-6">
      <div className="w-full max-w-md rounded-[14px] border border-[var(--line)] bg-[var(--paper)] p-8">
        <div className="flex items-center gap-1.5">
          <Image
            src="/brand/propensify-lockup.svg"
            alt="Propensify"
            width={160}
            height={40}
            priority
            className="h-[30px] w-auto"
          />
          <span
            aria-hidden="true"
            className="rounded bg-[var(--ink)] px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-[0.08em] text-white"
          >
            Beta
          </span>
        </div>

        {phase === 'form' ? (
          <div className="mt-6 space-y-5">
            <div>
              <h1 className="text-xl font-semibold text-[var(--ink)]">Create your workspace</h1>
              <p className="mt-1 text-sm text-[var(--body)]">
                Your personal workspace comes with 500 welcome credits. No company needed.
              </p>
              {email ? (
                <p className="mt-2 text-xs text-[var(--faint)]">
                  Creating as <span className="font-medium text-[var(--body)]">{email}</span> —{' '}
                  <Link
                    href="/sign-up"
                    className="underline underline-offset-2 hover:text-[var(--ink)]"
                  >
                    wrong account?
                  </Link>
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="workspace-name" className="text-sm font-medium text-[var(--ink)]">
                Workspace name
              </label>
              <input
                id="workspace-name"
                type="text"
                maxLength={255}
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                className="w-full rounded-md border border-[var(--line)] bg-white px-3 py-2 text-sm text-[var(--ink)] focus:border-[var(--ink)] focus:outline-none"
              />
            </div>

            <label className="flex cursor-pointer items-start gap-2.5 text-sm text-[var(--body)]">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ink)]"
              />
              <span>
                I accept the Propensify{' '}
                <a
                  href="https://propensify.io/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-[var(--ink)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://propensify.io/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-[var(--ink)]"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>

            <TurnstileWidget ref={turnstileRef} />

            {error ? (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            ) : null}

            <Button onClick={handleSubmit} disabled={busy} className="w-full">
              Create workspace
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--ink)]" />
            <p className="text-sm font-medium text-[var(--ink)]">
              {phase === 'provisioning' ? 'Setting up your workspace…' : 'Opening your workspace…'}
            </p>
            <p className="text-[13px] text-[var(--body)]">This usually takes a few seconds.</p>
          </div>
        )}
      </div>
    </div>
  );
}
